import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { generateFromBlog, type Platform } from '../../lib/api';
import LanguageSelector from '../../components/LanguageSelector';

type TabType = 'url' | 'text' | 'file';

/**
 * Create content from blog URL, pasted text, or file upload
 */
export default function CreateFromBlogPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('url');

  // Form state
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>(['linkedin', 'instagram']);
  const [language, setLanguage] = useState('en'); // Default to English, will use workspace default if available
  const [isLoading, setIsLoading] = useState(false);

  const platforms: { id: Platform; label: string; icon: string }[] = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
    { id: 'facebook', label: 'Facebook', icon: '👥' },
    { id: 'twitter', label: 'Twitter/X', icon: '🐦' },
    { id: 'youtube', label: 'YouTube', icon: '📹' },
    { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  ];

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  // Validation
  const isUrlValid = url.length >= 10 && url.startsWith('http');
  const isTextValid = text.length >= 50 && text.length <= 10000;
  const isFileValid = file !== null && ['text/plain', 'text/markdown', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type);

  const isValid =
    selectedPlatforms.length > 0 &&
    ((activeTab === 'url' && isUrlValid) ||
     (activeTab === 'text' && isTextValid) ||
     (activeTab === 'file' && isFileValid));

  const handleGenerate = async () => {
    if (!isValid) return;
    setIsLoading(true);

    try {
      let blogText = '';

      // Handle file upload
      if (activeTab === 'file' && file) {
        const reader = new FileReader();
        blogText = await new Promise<string>((resolve, reject) => {
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsText(file);
        });
      }

      // API now returns job ID instead of post
      const { jobId } = await generateFromBlog({
        url: activeTab === 'url' ? url : undefined,
        text: activeTab === 'text' ? text : activeTab === 'file' ? blogText : undefined,
        platforms: selectedPlatforms,
        title: title || undefined,
        language: language || undefined,
      });

      toast.success(t('create.blog.jobStarted', { defaultValue: 'Content generation started! Redirecting to Approve Board...' }));

      // Navigate to approve board with job ID
      navigate(`/${workspaceSlug}/approve`, {
        state: { activeJobIds: [jobId] }
      });
    } catch (error: any) {
      console.error('Failed to generate from blog:', error);

      // Handle 429 rate limit error specifically
      if (error.response?.status === 429) {
        const { activeJobs, limit } = error.response.data;
        toast.error(
          `You have ${activeJobs} active jobs. Maximum is ${limit}. Please wait for a job to complete.`
        );
      } else {
        toast.error(
          error.response?.data?.error || t('create.blog.error')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Estimate token cost (rough estimate based on content length)
  const estimateTokens = () => {
    const baseTokens = 500; // Platform generation overhead
    let contentTokens = 0;

    if (activeTab === 'url') {
      contentTokens = 2000; // Assume average blog is ~2000 tokens
    } else if (activeTab === 'text') {
      contentTokens = Math.ceil(text.length / 4); // ~4 chars per token
    } else if (activeTab === 'file' && file) {
      contentTokens = Math.ceil(file.size / 4); // Rough estimate
    }

    return baseTokens + contentTokens + selectedPlatforms.length * 150;
  };

  const tabs = [
    { id: 'url' as TabType, label: t('create.blog.tabs.url'), icon: '🔗' },
    { id: 'text' as TabType, label: t('create.blog.tabs.text'), icon: '📝' },
    { id: 'file' as TabType, label: t('create.blog.tabs.file'), icon: '📄' },
  ];

  return (
    <div className="max-w-4xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(`/${workspaceSlug}/dashboard`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t('create.backToDashboard')}
        </button>

        <h1 className="text-4xl font-bold mb-3 dark:text-gray-100">
          {t('create.blog.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t('create.blog.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="flex gap-2 border-b-2 border-black dark:border-gray-600">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                px-6 py-3 font-bold text-lg border-2 border-b-0 border-black dark:border-gray-600 rounded-t-lg transition-all
                ${activeTab === tab.id
                  ? 'bg-black dark:bg-white text-white dark:text-black -mb-[2px]'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }
              `}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* URL Tab */}
        {activeTab === 'url' && (
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.blog.urlLabel')}
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('create.blog.urlPlaceholder')}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
              disabled={isLoading}
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {t('create.blog.urlHint')}
            </p>
          </div>
        )}

        {/* Text Tab */}
        {activeTab === 'text' && (
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.blog.textLabel')}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('create.blog.textPlaceholder')}
              rows={12}
              className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 resize-none dark:bg-gray-700 dark:text-gray-100"
              disabled={isLoading}
            />
            <div className="flex justify-between text-sm mt-2">
              <p className="text-gray-600 dark:text-gray-400">
                {t('create.blog.textHint')}
              </p>
              <span
                className={`font-medium ${
                  text.length < 50 || text.length > 10000
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                {text.length} / 10,000
              </span>
            </div>
          </div>
        )}

        {/* File Tab */}
        {activeTab === 'file' && (
          <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
            <label className="block mb-2 font-bold text-lg dark:text-gray-100">
              {t('create.blog.fileLabel')}
            </label>
            <div className="border-2 border-dashed border-black dark:border-gray-600 rounded-lg p-8 text-center">
              {file ? (
                <div className="space-y-4">
                  <div className="text-6xl">📄</div>
                  <p className="font-bold text-lg dark:text-gray-100">{file.name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    onClick={() => setFile(null)}
                    className="px-4 py-2 border-2 border-black dark:border-gray-600 rounded font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-100"
                    disabled={isLoading}
                  >
                    {t('create.blog.removeFile')}
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".txt,.md,.docx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <div className="text-6xl mb-4">📤</div>
                  <p className="font-bold text-lg mb-2 dark:text-gray-100">
                    {t('create.blog.fileUpload')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('create.blog.fileHint')}
                  </p>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Optional Title */}
        <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
          <label className="block mb-2 font-bold text-lg dark:text-gray-100">
            {t('create.blog.titleLabel')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('create.blog.titlePlaceholder')}
            className="w-full px-4 py-3 border-2 border-black dark:border-gray-600 rounded-lg text-lg focus:outline-none focus:ring-4 focus:ring-black/20 dark:focus:ring-white/20 dark:bg-gray-700 dark:text-gray-100"
            disabled={isLoading}
          />
        </div>

        {/* Language Selection */}
        <LanguageSelector
          value={language}
          onChange={setLanguage}
          label="Content Language"
          description="Choose the language for AI-generated content"
        />

        {/* Platform Selection */}
        <div className="bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 rounded-lg p-6">
          <label className="block mb-4 font-bold text-lg dark:text-gray-100">
            {t('create.idea.platformsLabel')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {platforms.map((platform) => (
              <button
                key={platform.id}
                onClick={() => togglePlatform(platform.id)}
                className={`
                  p-4 border-2 rounded-lg font-medium transition-all
                  ${selectedPlatforms.includes(platform.id)
                    ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-brutal'
                    : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-black dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }
                `}
                disabled={isLoading}
              >
                <span className="text-2xl mr-2">{platform.icon}</span>
                {platform.label}
              </button>
            ))}
          </div>
        </div>

        {/* Token Cost Estimate */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold dark:text-gray-100">{t('create.idea.estimatedCost')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('create.idea.costNote')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold dark:text-gray-100">~{estimateTokens()}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('create.idea.tokens')}</p>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={!isValid || isLoading}
          className={`
            w-full py-4 px-6 rounded-lg font-bold text-lg border-2 transition-all
            ${!isValid || isLoading
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 border-gray-400 dark:border-gray-600 cursor-not-allowed'
              : 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white hover:shadow-brutal active:shadow-none'
            }
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('create.idea.generating')}
            </span>
          ) : (
            t('create.blog.generateButton')
          )}
        </button>
      </div>
    </div>
  );
}
